import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ShieldCheck, Pencil, Trash2, LogOut, CircleCheck, Heart, Stethoscope,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MOBILITY_OPTIONS, SENSORY_OPTIONS, DISCLAIMER_FULL } from "@/lib/constants";

function Field({ label, value }) {
  return (
    <div className="py-3 border-b border-[#333333] last:border-0">
      <p className="text-sm text-[#A1A1AA]">{label}</p>
      <p className="text-[#F8FAFC] font-medium">{value || "—"}</p>
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data));
  }, []);

  const deleteAll = async () => {
    try {
      await api.delete("/me");
      toast.success("All your data has been deleted.");
      logout();
      navigate("/welcome");
    } catch {
      toast.error("Could not delete data. Please try again.");
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/welcome");
  };

  const mobLabel = MOBILITY_OPTIONS.find((m) => m.value === profile?.mobility_needs)?.label;
  const sensLabels = (profile?.sensory_needs || [])
    .map((s) => SENSORY_OPTIONS.find((o) => o.value === s)?.label)
    .filter(Boolean)
    .join(", ");
  const consentDate = user?.consent_timestamp
    ? new Date(user.consent_timestamp).toLocaleString()
    : null;

  return (
    <AppShell active="profile">
      <h1 className="font-head text-3xl font-extrabold text-[#F8FAFC]">Profile</h1>
      <p className="text-[#A1A1AA] mt-1">{user?.email}</p>

      {/* Back to Care */}
      <button
        data-testid="profile-switch-mode-btn"
        onClick={() => navigate("/care")}
        className="mt-4 w-full flex items-center justify-between bg-[#1A1A1A] border border-[#333333] rounded-2xl px-5 py-4 min-h-[56px]"
      >
        <span className="flex items-center gap-2 text-[#F8FAFC] font-semibold">
          <Stethoscope size={18} className="text-[#EF9F27]" /> Back to Care
        </span>
        <span className="text-sm text-[#A1A1AA]">Second opinions</span>
      </button>

      {/* Health profile */}
      <div className="mt-5 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Heart size={18} className="text-[#EF9F27]" />
            <h2 className="font-head text-lg font-bold text-[#F8FAFC]">Health profile</h2>
          </div>
          <Button
            data-testid="edit-profile-btn"
            onClick={() => navigate("/profile/edit")}
            variant="outline"
            className="min-h-[44px] rounded-xl border-2 border-[#1A1A1A] text-[#F8FAFC]"
          >
            <Pencil size={16} className="mr-1" /> Edit
          </Button>
        </div>
        <Field label="Conditions" value={(profile?.conditions || []).join(", ")} />
        <Field label="Medications" value={(profile?.medications || []).join(", ")} />
        <Field label="Allergies" value={(profile?.allergies || []).join(", ")} />
        <Field label="Mobility" value={mobLabel} />
        <Field label="Sensory needs" value={sensLabels} />
        <Field label="Emergency contact" value={profile?.emergency_contact_name ? `${profile.emergency_contact_name} — ${profile.emergency_contact_phone}` : ""} />
      </div>

      {/* Consent */}
      <div className="mt-4 bg-[#EF9F27]/15 rounded-2xl p-4" data-testid="consent-status">
        <div className="flex items-center gap-2">
          <CircleCheck size={18} className="text-[#EF9F27]" />
          <p className="font-semibold text-[#F8FAFC]">Health-data consent given</p>
        </div>
        {consentDate && <p className="text-sm text-[#A1A1AA] mt-1">On {consentDate}</p>}
      </div>

      {/* Danger zone */}
      <div className="mt-4 bg-[#1A1A1A] border border-red-200 rounded-2xl p-5">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              data-testid="delete-data-btn"
              className="w-full min-h-[56px] rounded-xl bg-[#D32F2F] hover:bg-[#b71c1c] text-white font-semibold"
            >
              <Trash2 size={18} className="mr-2" /> Delete all my data
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="max-w-[360px] rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete all your data?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently deletes your account and all data across both modes — health profile,
                documents, trips and reports, and any Care cases and consultations. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="min-h-[48px] rounded-xl">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-delete-btn"
                onClick={deleteAll}
                className="min-h-[48px] rounded-xl bg-[#D32F2F] hover:bg-[#b71c1c]"
              >
                Delete everything
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* About */}
      <div className="mt-4 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={18} className="text-[#EF9F27]" />
          <h2 className="font-head text-lg font-bold text-[#F8FAFC]">About & disclaimer</h2>
        </div>
        <p className="text-sm text-[#A1A1AA] leading-relaxed">{DISCLAIMER_FULL}</p>
      </div>

      <div className="mt-4 flex items-center justify-center gap-4 text-sm text-[#A1A1AA]">
        <button data-testid="profile-privacy-link" onClick={() => navigate("/privacy")} className="underline underline-offset-2 min-h-[44px]">
          Privacy Policy
        </button>
        <span className="text-[#333333]">·</span>
        <button data-testid="profile-terms-link" onClick={() => navigate("/terms")} className="underline underline-offset-2 min-h-[44px]">
          Terms
        </button>
      </div>

      <Button
        data-testid="logout-btn"
        onClick={doLogout}
        variant="ghost"
        className="mt-4 w-full min-h-[52px] rounded-xl text-[#A1A1AA]"
      >
        <LogOut size={18} className="mr-2" /> Log out
      </Button>
    </AppShell>
  );
}
